import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { safeSetItem } from "@/lib/safeStorage";

const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "es", label: "Español", short: "ES" },
  { code: "fr", label: "Français", short: "FR" },
];

interface LanguagePillProps {
  light?: boolean;
}

export default function LanguagePill({ light = false }: LanguagePillProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  function selectLang(code: string) {
    void i18n.changeLanguage(code);
    safeSetItem("db-lang", code);
    setOpen(false);
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const pillColor = light
    ? "rgba(255,255,255,0.12)"
    : "rgba(0,0,0,0.05)";
  const pillBorder = light
    ? "rgba(255,255,255,0.18)"
    : "rgba(0,0,0,0.10)";
  const pillText = light ? "rgba(255,255,255,0.75)" : "hsl(var(--muted-foreground))";
  const dropBg = "hsl(var(--popover))";
  const dropBorder = "hsl(var(--border))";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Select language"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "5px 10px",
          borderRadius: "20px",
          background: pillColor,
          border: `1px solid ${pillBorder}`,
          color: pillText,
          fontSize: "12px",
          fontWeight: 400,
          letterSpacing: "0.04em",
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = light
            ? "rgba(255,255,255,0.18)"
            : "rgba(0,0,0,0.08)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = pillColor;
        }}
      >
        {current.short}
        <ChevronDown
          style={{
            width: "11px",
            height: "11px",
            opacity: 0.6,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            background: dropBg,
            border: `1px solid ${dropBorder}`,
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            overflow: "hidden",
            zIndex: 200,
            minWidth: "130px",
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === i18n.language;
            return (
              <button
                key={lang.code}
                onClick={() => selectLang(lang.code)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "9px 14px",
                  background: isActive ? "hsl(var(--accent))" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: isActive ? 500 : 300,
                  color: "hsl(var(--foreground))",
                  fontFamily: "inherit",
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = "hsl(var(--accent))";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                <span>{lang.label}</span>
                <span style={{ fontSize: "10px", color: "hsl(var(--muted-foreground))", fontWeight: 400, letterSpacing: "0.05em" }}>
                  {lang.short}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
